import { useEffect, useState } from "react";
import { Box, Typography, Chip, Alert, Collapse } from "@mui/material";
import RouterIcon from "@mui/icons-material/Router";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useNavigate } from "react-router-dom";

export default function DeviceHealth({ data }) {
  const navigate = useNavigate();
  const health = data?.deviceHealthSummary;
  const devices = health?.devices || [];
  const offlineDevices = devices.filter((d) => !d.isOnline);
  const [showAlert, setShowAlert] = useState(false);

  // Show pulsing alert when any device is offline
  useEffect(() => {
    if (offlineDevices.length > 0) {
      setShowAlert(true);
    } else {
      setShowAlert(false);
    }
  }, [offlineDevices.length]);

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "16px",
        p: 2,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        height: "100%",
      }}
    >
      {/* Offline Alert Banner */}
      <Collapse in={showAlert}>
        <Alert
          severity="error"
          icon={<WarningAmberIcon sx={{ animation: "pulse 1.5s infinite" }} />}
          sx={{
            mb: 2,
            borderRadius: "10px",
            fontWeight: 600,
            fontSize: "12px",
            "@keyframes pulse": {
              "0%": { opacity: 1 },
              "50%": { opacity: 0.4 },
              "100%": { opacity: 1 },
            },
          }}
          onClose={() => setShowAlert(false)}
        >
          {offlineDevices.length} device{offlineDevices.length > 1 ? "s" : ""}{" "}
          offline:{" "}
          {offlineDevices.map((d) => d.deviceName).join(", ")}
        </Alert>
      </Collapse>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "16px", color: "#333" }}>
          Device Health
        </Typography>
        {health && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Chip
              icon={<FiberManualRecordIcon sx={{ fontSize: 10 }} />}
              label={`${health.onlineCount} Online`}
              size="small"
              sx={{
                fontSize: "11px",
                height: "24px",
                backgroundColor: "#E8F5E9",
                color: "#2E7D32",
                "& .MuiChip-icon": { color: "#4CAF50" },
              }}
            />
            <Chip
              icon={<FiberManualRecordIcon sx={{ fontSize: 10 }} />}
              label={`${health.offlineCount} Offline`}
              size="small"
              sx={{
                fontSize: "11px",
                height: "24px",
                backgroundColor:
                  health.offlineCount > 0 ? "#FFEBEE" : "#F5F5F5",
                color: health.offlineCount > 0 ? "#C62828" : "#999",
                "& .MuiChip-icon": {
                  color: health.offlineCount > 0 ? "#EF5350" : "#BDBDBD",
                },
              }}
            />
          </Box>
        )}
      </Box>

      {devices.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: 1.5,
          }}
        >
          {devices.map((d, i) => (
            <Box
              key={i}
              onClick={() => navigate("/device")}
              sx={{
                border: `1px solid ${d.isOnline ? "#C8E6C9" : "#FFCDD2"}`,
                borderRadius: "12px",
                p: 1.5,
                backgroundColor: d.isOnline ? "#FAFFF9" : "#FFFAFA",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.5,
                }}
              >
                <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                  {d.deviceName}
                </Typography>
                <FiberManualRecordIcon
                  sx={{
                    fontSize: 12,
                    color: d.isOnline ? "#4CAF50" : "#EF5350",
                    animation: d.isOnline ? "none" : "pulse 1.5s infinite",
                    "@keyframes pulse": {
                      "0%": { opacity: 1 },
                      "50%": { opacity: 0.3 },
                      "100%": { opacity: 1 },
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", gap: 0.5, mb: 0.5 }}>
                <Chip
                  label={d.branch || "\u2014"}
                  size="small"
                  sx={{
                    fontSize: "10px",
                    height: "20px",
                    backgroundColor: "#E3F2FD",
                    color: "#1565C0",
                  }}
                />
                <Chip
                  label={d.direction}
                  size="small"
                  sx={{
                    fontSize: "10px",
                    height: "20px",
                    backgroundColor:
                      d.direction === "IN" ? "#E8F5E9" : "#E3F2FD",
                    color: d.direction === "IN" ? "#2E7D32" : "#1565C0",
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: "10px",
                  color: d.isOnline ? "#999" : "#C62828",
                  fontWeight: d.isOnline ? 400 : 600,
                }}
              >
                {d.isOnline
                  ? `Last seen: ${d.lastSeen || "Never"}`
                  : d.lastSeen
                  ? `Offline since: ${d.lastSeen}`
                  : "Never connected"}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <RouterIcon sx={{ fontSize: 40, color: "#ddd", mb: 1 }} />
          <Typography color="textSecondary">
            No devices registered
          </Typography>
        </Box>
      )}
    </Box>
  );
}
